import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import type {
    ConversionRatesOvertimeVisitsListData,
    ConversionRatesOvertimeVisitsListParams,
    ConversionRatesOvertimeVisitsPopListData,
    ConversionRatesOvertimeVisitsPopListParams,
} from "@similarweb/pro-api-client";

import DurationServiceSingleton from "app/services/DurationService";

import type { ConversionRateOverTimePoint, ConversionRatesParams } from "./types";

export const getSeriesColor = (site: string, index: number, getSiteColor?: (site: string) => string): StrictColor => {
    if (getSiteColor) {
        return getSiteColor(site) as StrictColor;
    }

    const fallbackColors: StrictColor[] = [
        colorsPalettes.blue[400],
        colorsPalettes.sky[300],
        colorsPalettes.orange[400],
        colorsPalettes.mint[400],
    ];

    return fallbackColors[index % fallbackColors.length];
};

const createBaseParams = (params: ConversionRatesParams) => {
    const { duration, comparedDuration, key, isWWW } = params;
    const { from, to, compareFrom, compareTo } = DurationServiceSingleton.getDurationData(
        duration,
        comparedDuration,
    ).forAPI;

    const country = Number(params.country);

    return {
        country: Number.isFinite(country) ? country : 999,
        keys: (key ?? "").split(",").filter(Boolean),
        from,
        to,
        compareFrom,
        compareTo,
        includeSubdomains: isWWW === "*",
    } satisfies ConversionRatesOvertimeVisitsPopListParams;
};

export const createParams = (params: ConversionRatesParams): ConversionRatesOvertimeVisitsListParams => {
    const { compareFrom, compareTo, ...singleParams } = createBaseParams(params);

    return singleParams;
};

export const createPopParams = (params: ConversionRatesParams): ConversionRatesOvertimeVisitsPopListParams => {
    const { compareFrom, compareTo, ...base } = createBaseParams(params);

    return {
        ...base,
        compareFrom: compareFrom ?? "",
        compareTo: compareTo ?? "",
    };
};

export const getPointValue = (point: ConversionRateOverTimePoint): number | null => {
    return point.value == null ? null : Number(point.value);
};

export const getCompareValue = (point: ConversionRateOverTimePoint): number | null => {
    return point.compareValue == null ? null : Number(point.compareValue);
};

export type NormalizedSiteData = {
    site: string;
    data: ConversionRateOverTimePoint[];
    averageVisits?: number | null;
};

export const selectListData = (rawData: ConversionRatesOvertimeVisitsListData): NormalizedSiteData[] => {
    return rawData.map((item) => ({
        site: item.site,
        data: item.data,
        averageVisits: item.averageVisits,
    }));
};

export const selectPopData = (rawData: ConversionRatesOvertimeVisitsPopListData): NormalizedSiteData[] => {
    return rawData.map((item) => ({
        site: item.site,
        data: item.data,
        averageVisits: item.averageVisits,
    }));
};
